import {
  TOURNAMENT_AGE_MAX_UNRESTRICTED,
  TOURNAMENT_AGE_MIN_UNRESTRICTED,
} from '@/types/tournament.model';

/** Subset of the team-config draft slice used to compute effective ages. */
export type TournamentAgeDraftSlice = {
  minAgeRestrictionEnabled?: boolean;
  maxAgeRestrictionEnabled?: boolean;
  minAge?: number;
  maxAge?: number;
} | null | undefined;

export function normalizeTournamentAges(
  minAge?: number | null,
  maxAge?: number | null,
): { minAge: number; maxAge: number } {
  return {
    minAge: minAge ?? TOURNAMENT_AGE_MIN_UNRESTRICTED,
    maxAge: maxAge ?? TOURNAMENT_AGE_MAX_UNRESTRICTED,
  };
}

/**
 * Parses checkbox + raw input strings into API min/max ages.
 * Returns null if a restriction is enabled but the input is empty/invalid, or if min exceeds max.
 */
export function parseTournamentAgeApiPayload(
  minRestricted: boolean,
  maxRestricted: boolean,
  minAgeInput: string,
  maxAgeInput: string,
): { minAge: number; maxAge: number } | null {
  let effMin = TOURNAMENT_AGE_MIN_UNRESTRICTED;
  let effMax = TOURNAMENT_AGE_MAX_UNRESTRICTED;

  if (minRestricted) {
    const t = minAgeInput.trim();
    if (t === '') return null;
    const n = parseInt(t, 10);
    if (!Number.isFinite(n) || n < 0) return null;
    effMin = Math.floor(n);
  }

  if (maxRestricted) {
    const t = maxAgeInput.trim();
    if (t === '') return null;
    const n = parseInt(t, 10);
    if (!Number.isFinite(n) || n < 0) return null;
    effMax = Math.floor(n);
  }

  if (effMin > effMax) return null;
  return { minAge: effMin, maxAge: effMax };
}

/** True when both limits are restricted, both inputs are valid numbers, and min exceeds max. */
export function tournamentAgeInputsShowRangeConflict(
  minRestricted: boolean,
  maxRestricted: boolean,
  minAgeInput: string,
  maxAgeInput: string,
): boolean {
  if (!minRestricted || !maxRestricted) return false;
  const t1 = minAgeInput.trim();
  const t2 = maxAgeInput.trim();
  if (t1 === '' || t2 === '') return false;
  const a = parseInt(t1, 10);
  const b = parseInt(t2, 10);
  return (
    Number.isFinite(a) &&
    Number.isFinite(b) &&
    a >= 0 &&
    b >= 0 &&
    a > b
  );
}

/** Maps persisted tournament ages into wizard/edit draft fields. */
export function tournamentToAgeRestrictionDraft(tournament: {
  minAge?: number;
  maxAge?: number;
}): Exclude<TournamentAgeDraftSlice, null | undefined> {
  const minA = tournament.minAge ?? TOURNAMENT_AGE_MIN_UNRESTRICTED;
  const maxA = tournament.maxAge ?? TOURNAMENT_AGE_MAX_UNRESTRICTED;
  return {
    minAgeRestrictionEnabled: minA !== TOURNAMENT_AGE_MIN_UNRESTRICTED,
    maxAgeRestrictionEnabled: maxA !== TOURNAMENT_AGE_MAX_UNRESTRICTED,
    minAge: minA !== TOURNAMENT_AGE_MIN_UNRESTRICTED ? minA : undefined,
    maxAge: maxA !== TOURNAMENT_AGE_MAX_UNRESTRICTED ? maxA : undefined,
  };
}

export type TournamentAgeRestrictionPatch = Partial<{
  minAgeRestrictionEnabled: boolean;
  maxAgeRestrictionEnabled: boolean;
  minAge: number | undefined;
  maxAge: number | undefined;
}>;

/** Effective min/max ages sent to the API from the team-config draft. */
export function effectiveAgesFromTeamConfig(
  tc: TournamentAgeDraftSlice,
): { minAge: number; maxAge: number } {
  if (!tc) {
    return {
      minAge: TOURNAMENT_AGE_MIN_UNRESTRICTED,
      maxAge: TOURNAMENT_AGE_MAX_UNRESTRICTED,
    };
  }
  const minR = tc.minAgeRestrictionEnabled === true;
  const maxR = tc.maxAgeRestrictionEnabled === true;
  return {
    minAge: minR
      ? (tc.minAge ?? TOURNAMENT_AGE_MIN_UNRESTRICTED)
      : TOURNAMENT_AGE_MIN_UNRESTRICTED,
    maxAge: maxR
      ? (tc.maxAge ?? TOURNAMENT_AGE_MAX_UNRESTRICTED)
      : TOURNAMENT_AGE_MAX_UNRESTRICTED,
  };
}

/**
 * Display strings for summary / detail. Single line when both limits match defaults.
 */
export function getTournamentAgeDisplayLines(
  minAge?: number | null,
  maxAge?: number | null,
): string[] {
  const { minAge: minV, maxAge: maxV } = normalizeTournamentAges(minAge, maxAge);
  if (
    minV === TOURNAMENT_AGE_MIN_UNRESTRICTED &&
    maxV === TOURNAMENT_AGE_MAX_UNRESTRICTED
  ) {
    return ['Sin restricción de edad'];
  }
  const lines: string[] = [];
  lines.push(
    minV === TOURNAMENT_AGE_MIN_UNRESTRICTED
      ? 'Edad mínima: sin restricción'
      : `Edad mínima: ${minV} años`,
  );
  lines.push(
    maxV === TOURNAMENT_AGE_MAX_UNRESTRICTED
      ? 'Edad máxima: sin restricción'
      : `Edad máxima: ${maxV} años`,
  );
  return lines;
}

/** Short label for cards and list views. */
export function getTournamentAgeCompactLabel(
  minAge?: number | null,
  maxAge?: number | null,
): string {
  const { minAge: minV, maxAge: maxV } = normalizeTournamentAges(minAge, maxAge);
  if (
    minV === TOURNAMENT_AGE_MIN_UNRESTRICTED &&
    maxV === TOURNAMENT_AGE_MAX_UNRESTRICTED
  ) {
    return 'Sin restricción de edad';
  }
  const minPart =
    minV === TOURNAMENT_AGE_MIN_UNRESTRICTED ? 'sin mín.' : `${minV}`;
  const maxPart =
    maxV === TOURNAMENT_AGE_MAX_UNRESTRICTED ? 'sin máx.' : `${maxV}`;
  return `Edad: ${minPart} – ${maxPart}`;
}
